import { useCallback, useEffect, useRef, useState } from "react";
import { TaskEditorState } from "./taskEditorUtils";
import { cloneTaskEditorState } from "./taskEditorDiff";

const UNDO_HISTORY_LIMIT = 49;
const TEXT_EDIT_GROUP_RESET_MS = 500;

type UseTaskEditorUndoHistoryArgs = {
  getCurrentState: () => TaskEditorState;
  applyState: (state: TaskEditorState) => void;
};

export type UseTaskEditorUndoHistoryReturn = {
  pushSnapshot: (state?: TaskEditorState) => void;
  pushSnapshotForTextEdit: (groupKey: string, state?: TaskEditorState) => void;
  resetTextEditGrouping: () => void;
  undo: () => boolean;
  clear: () => void;
  hasHistory: boolean;
};

export function useTaskEditorUndoHistory({
  getCurrentState,
  applyState,
}: UseTaskEditorUndoHistoryArgs): UseTaskEditorUndoHistoryReturn {
  const [history, setHistory] = useState<TaskEditorState[]>([]);
  const textEditGroupRef = useRef<string | null>(null);
  const textEditResetTimerRef = useRef<number | undefined>(undefined);

  const resetTextEditGrouping = useCallback(() => {
    textEditGroupRef.current = null;

    if (textEditResetTimerRef.current !== undefined) {
      window.clearTimeout(textEditResetTimerRef.current);
      textEditResetTimerRef.current = undefined;
    }
  }, []);

  const scheduleTextEditGroupingReset = useCallback(() => {
    if (textEditResetTimerRef.current !== undefined) {
      window.clearTimeout(textEditResetTimerRef.current);
    }

    textEditResetTimerRef.current = window.setTimeout(() => {
      textEditGroupRef.current = null;
      textEditResetTimerRef.current = undefined;
    }, TEXT_EDIT_GROUP_RESET_MS);
  }, []);

  const pushSnapshot = useCallback(
    (state?: TaskEditorState) => {
      const sourceState = state ?? getCurrentState();
      const nextSnapshot = cloneTaskEditorState(sourceState);
      const nextSerializedSnapshot = JSON.stringify(nextSnapshot);

      setHistory((currentHistory) => {
        if (
          currentHistory.length > 0 &&
          JSON.stringify(currentHistory[currentHistory.length - 1]) ===
            nextSerializedSnapshot
        ) {
          return currentHistory;
        }

        return [...currentHistory.slice(-UNDO_HISTORY_LIMIT), nextSnapshot];
      });
    },
    [getCurrentState]
  );

  const pushSnapshotForTextEdit = useCallback(
    (groupKey: string, state?: TaskEditorState) => {
      if (textEditGroupRef.current !== groupKey) {
        pushSnapshot(state);
        textEditGroupRef.current = groupKey;
      }

      scheduleTextEditGroupingReset();
    },
    [pushSnapshot, scheduleTextEditGroupingReset]
  );

  const undo = useCallback(() => {
    const previousState = history[history.length - 1];

    if (!previousState) {
      return false;
    }

    resetTextEditGrouping();
    setHistory((currentHistory) => currentHistory.slice(0, -1));
    applyState(previousState);

    return true;
  }, [applyState, history, resetTextEditGrouping]);

  const clear = useCallback(() => {
    setHistory([]);
  }, []);

  useEffect(() => {
    return () => {
      if (textEditResetTimerRef.current !== undefined) {
        window.clearTimeout(textEditResetTimerRef.current);
      }
    };
  }, []);

  return {
    pushSnapshot,
    pushSnapshotForTextEdit,
    resetTextEditGrouping,
    undo,
    clear,
    hasHistory: history.length > 0,
  };
}
